import React, { lazy, Suspense, useEffect, useMemo, useState } from 'react';
import { Switch, Route, BrowserRouter as Router } from 'react-router-dom';
import PageLoading from './UI/routing/PageLoading';
import { mockMenus } from './mockData/mockData';
import UserWrapper from './appBarContent/UserWrapper';
import AppBarControls from './appBarContent/AppBarControls';
import {
  useAxiosGet,
  useUser,
  useIsUnityAuthenticated,
  useUserActions,
  LoadingIndicator,
  PageContainer,
} from 'unity-fluent-library';
import AadCallback from './routes/callback/AadCallback';
import { useTheme } from '@material-ui/core';
import { SnackbarProvider } from 'notistack';
import ShellWrapper from './ShellWrapper';
import { ShellContext } from './contexts/ShellContext';
import PrivateRoute from './routes/PrivateRoute';
import ErrorBoundary from './ErrorBoundary';
import ZeroTenantAccessDialog from './UI/routing/ZeroTenantAccessDialog';
import getWidget, { isValidWidget } from './dashboard/getWidget';

const PageNotFound = lazy(() => import('./UI/routing/PageNotFoundRoute.js'));
const LocalLogin = lazy(() => import('./routes/auth/LocalLogin'));
const TestPage = lazy(() => import('./routes/test/TestPage'));
const TestShell = lazy(() => import('./routes/test/TestShell'));
const Inquiry = lazy(() => import('./routes/inquiry/Inquiry'));
const TableBrowser = lazy(() => import('./routes/tableBrowser/TableBrowser'));
const CrudBrowser = lazy(() => import('./routes/crudBrowser/CrudBrowser'));
const PaymentTransactions = lazy(() => import('./pages/PaymentTransactions'));
const PaymentSettingsPage = lazy(() => import('./pages/PaymentSettingsPage'));
const PaymentAccountPage = lazy(() => import('./pages/PaymentAccountPage'));
const BatchPaymentPage = lazy(() => import('./pages/BatchPaymentPage'));
const PaymentRefundsPage = lazy(() => import('./pages/PaymentRefundsPage'));
const PaymentReversalsPage = lazy(() => import('./pages/PaymentReversalsPage'));

const Reports = lazy(() => import('./routes/reports/Reports'));

const IS_LOCAL_AUTH = process.env.REACT_APP_LOCAL_AUTH === 'true';

const Routes = () => {
  const user = useUser();
  const [menuToggle, setMenuToggle] = useState(true);
  const [inquiryMenuToggle, setInquiryMenuToggle] = useState(true);
  const [headerExpander, setHeaderExpander] = useState(false);
  const [headerTitle, setHeaderTitle] = useState('');
  const [secondExtendedWindow, setSecondExtendedWindow] = useState(false);
  const [showToggle1, setShowToggle1] = useState(false);
  const [showToggle2, setShowToggle2] = useState(false);
  const [primaryExpanded, setPrimaryExpanded] = useState(false);
  const [secondaryExpanded, setSecondaryExpanded] = useState(true);
  const [utilityBarHasTabsOpen, setUtilityBarHasTabsOpen] = useState(false);
  const [zeroTenantAccess, setZeroTenantAccess] = useState(false);
  const theme = useTheme();
  const { setUser } = useUserActions();
  const isAuthenticated = useIsUnityAuthenticated();

  const useMockData = process.env.REACT_APP_USE_MOCKS === 'true';

  const [{ data: leftMenu }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `menus?menuTypeId=1&productId=${process.env.REACT_APP_UNITY_PRODUCT_ID}&tenantId=${user?.currentTenantId}`,
    {},
    !!!user?.currentTenantId
  );

  const [{ data: appSelectorMenus }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `menus?tenantId=${user?.currentTenantId}&verticalId=${
      process.env.REACT_APP_UNITY_VERTICAL_ID
    }&userId=${user ? user?.id : ''}`,
    {},
    !!!user?.id
  );

  const [{ data: currentTenant, error: getTenantError }, refetchCurrentTenant] =
    useAxiosGet(
      process.env.REACT_APP_TENANTS_API_BASE,
      `tenants/${user?.currentTenantId}`,
      {},
      true
    );

  const [{ data: userTenants }] = useAxiosGet(
    process.env.REACT_APP_SECURITY_API_BASE,
    `users/${user ? user?.id : ''}/unitytenants`,
    {},
    !!!user?.id
  );

  const [{ data: languages }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `language`,
    {}
  );

  const [{ data: defaultProductList }] = useAxiosGet(
    process.env.REACT_APP_TENANTS_API_BASE,
    `products`,
    {}
  );

  const enableFilter = () => {
    if (defaultProductList != null) {
      let matchingProduct = defaultProductList.filter(
        prod =>
          prod.productId === parseInt(process.env.REACT_APP_UNITY_PRODUCT_ID)
      );
      if (matchingProduct[0].isSubscribable) {
        return filterTenants(userTenants);
      }
    }
    return userTenants;
  };

  const filterTenants = userTenants => {
    let tenantMap = new Map();
    let containsProduct = [];

    if (userTenants != null) {
      userTenants.forEach(tenant =>
        tenantMap.set(tenant.tenantId, tenant.products)
      );
    }

    tenantMap.forEach((value, key) => {
      let productArray = [];
      value.forEach(product => productArray.push(product.productId));

      if (
        productArray.includes(parseInt(process.env.REACT_APP_UNITY_PRODUCT_ID))
      ) {
        containsProduct.push({ tenantId: key });
      }
    });

    if (userTenants != null) {
      let myFilteredArray = userTenants.filter(original => {
        return containsProduct.some(ft => {
          return ft.tenantId === original.tenantId;
        });
      });

      return myFilteredArray;
    }
  };

  const userHasAccessToTenant = useMemo(() => {
    if (user?.tenantIds?.includes(user?.currentTenantId)) {
      return true;
    }
    return false;
  }, [user]);

  useEffect(() => {
    if (isAuthenticated && user?.currentTenantId) {
      if (!IS_LOCAL_AUTH && !user?.accessToken) {
        return;
      }
      refetchCurrentTenant().then(tenant => {
        const currentTenantTheme = tenant.data.theme;
        if (user?.statusCode === 200 && !user?.tenantIds.length) {
          setZeroTenantAccess(true);
          theme.setCurrentTheme(null);
        } else {
          if (!currentTenantTheme) {
            theme.setCurrentTheme(null);
          }
          if (theme.setCurrentTheme && currentTenantTheme !== undefined) {
            theme.setCurrentTheme(currentTenantTheme);
          }
        }
      });
    }
    // Don't need to include Theme as dependencies
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, refetchCurrentTenant, isAuthenticated]);

  if (
    (!theme.isInitialized && isAuthenticated && !getTenantError) ||
    (isAuthenticated && user?.statusCode !== 200)
  ) {
    return <LoadingIndicator />;
  }

  if (!IS_LOCAL_AUTH && zeroTenantAccess) {
    return <ZeroTenantAccessDialog open={true} />;
  }

  return (
    <Router basename={process.env.PUBLIC_URL}>
      <SnackbarProvider>
        <ShellContext.Provider
          value={{
            menuToggle,
            setMenuToggle,
            headerExpander,
            setHeaderExpander,
            headerTitle,
            setHeaderTitle,
            secondExtendedWindow,
            setSecondExtendedWindow,
            showToggle1,
            setShowToggle1,
            showToggle2,
            setShowToggle2,
            primaryExpanded,
            setPrimaryExpanded,
            secondaryExpanded,
            setSecondaryExpanded,
            inquiryMenuToggle,
            setInquiryMenuToggle,
            utilityBarHasTabsOpen,
            setUtilityBarHasTabsOpen,
          }}
        >
          <ShellWrapper
            userAvatar={
              <UserWrapper
                tenant={{
                  label: 'Current Tenant',
                  current: currentTenant,
                  options: enableFilter(),
                  onSelect: setUser,
                }}
                unityUrl={process.env.REACT_APP_UNITY_URL}
                user={user}
                userHasAccessToTenant={userHasAccessToTenant}
              />
            }
            appContent={<AppBarControls />}
            leftMenu={userHasAccessToTenant ? leftMenu : []}
            appSelectorMenus={userHasAccessToTenant ? appSelectorMenus : []}
            user={user}
            navRoot={'Home'}
            siteName="Ambient Starter"
            menuToggle={menuToggle}
            inquiryMenuToggle={inquiryMenuToggle}
            setInquiryMenuToggle={setInquiryMenuToggle}
            utilityBarHasTabsOpen={utilityBarHasTabsOpen}
            setUtilityBarHasTabsOpen={setUtilityBarHasTabsOpen}
            headerExpander={headerExpander}
            secondExtendedWindow={secondExtendedWindow}
            showToggle1={showToggle1}
            showToggle2={showToggle2}
            primaryExpanded={primaryExpanded}
            secondaryExpanded={secondaryExpanded}
            setPrimaryExpanded={setPrimaryExpanded}
            setSecondaryExpanded={setSecondaryExpanded}
            useMockData={useMockData}
            mockMenus={mockMenus}
            unityUrl={process.env.REACT_APP_UNITY_URL}
            hideLayout={!isAuthenticated && IS_LOCAL_AUTH}
            languages={languages}
          >
            <ErrorBoundary>
              <Suspense fallback={<PageLoading />}>
                <Switch>
                  <Route exact path="/aad_callback" component={AadCallback} />
                  {/* If you have different types of login based on .env */}
                  {!isAuthenticated && IS_LOCAL_AUTH && (
                    <Route path="/login" exact component={LocalLogin} />
                  )}
                  <PrivateRoute
                    path="/"
                    exact
                    component={PaymentTransactions}
                  />
                  <PrivateRoute path="/test" exact component={TestShell} />
                  <PrivateRoute
                    path="/paymenttransactions"
                    exact
                    component={PaymentTransactions}
                  />
                  <PrivateRoute
                    path="/paymentsettings"
                    exact
                    component={PaymentSettingsPage}
                  />
                  <PrivateRoute
                    path="/paymentaccount"
                    exact
                    component={PaymentAccountPage}
                  />
                  <PrivateRoute
                    path="/batchpayment"
                    exact
                    component={BatchPaymentPage}
                  />
                  <PrivateRoute
                    path="/paymentrefunds"
                    exact
                    component={PaymentRefundsPage}
                  />
                  <PrivateRoute
                    path="/paymentreversals"
                    exact
                    component={PaymentReversalsPage}
                  />
                  <PrivateRoute path="/reports/:id" exact component={Reports} />
                  <PrivateRoute
                    path="/tablebrowser"
                    render={props => <TableBrowser {...props} />}
                  />
                  <PrivateRoute
                    path="/tablebrowser/:entity"
                    render={props => <TableBrowser {...props} />}
                  />
                  <PrivateRoute
                    path="/crudbrowser"
                    render={props => <CrudBrowser {...props} />}
                  />
                  <PrivateRoute
                    path="/inquiry/:id"
                    render={props => <Inquiry {...props} />}
                  />
                  <PrivateRoute
                    path="/page/:id"
                    render={props => (
                      <PageContainer
                        getWidget={getWidget}
                        isValidWidget={isValidWidget}
                        {...props}
                      />
                    )}
                  />
                  <PrivateRoute path="*" component={PageNotFound} />
                </Switch>
              </Suspense>
            </ErrorBoundary>
          </ShellWrapper>
        </ShellContext.Provider>
      </SnackbarProvider>
    </Router>
  );
};
export default Routes;
